import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import { selectCurrentUser } from '@user/store/authorization'
import { RecordAtLocation } from '@user/store/map/typings'
import {
  saveGeneralPreferencesBatch,
  IUserPreferenceGeneralListItemType,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { useFilterChipData } from '@user/components/HereMap/hooks/useFilterChipData'
import { PREF } from '@user/components/HereMap/FilterChips/constants'
import { computeHiddenByFilters } from './recordsHiddenByFilters'

/**
 * SH-1143 — Given the records at the clicked coordinate (already fetched once by the click flow and
 * passed in), works out whether any are hidden by the user's active map filters and exposes an
 * action that removes only the relevant filters to reveal them all at once.
 *
 * It does not fetch — the click flow does the single lookup and hands the records in, so there's no
 * second backend round-trip. `enabled` should be true only for a committed empty-rooftop click.
 */
export function useRecordsHiddenByFilters(
  records: RecordAtLocation[] | null | undefined,
  enabled: boolean,
) {
  const dispatch = useAppDispatch()
  const currentUser = useSelector(selectCurrentUser)
  const { selectedTypes, selectedKnockStatusIds, myRecords } =
    useFilterChipData()

  // String signatures keep the memo stable across renders (filter arrays are rebuilt each render).
  const typesSig = selectedTypes.join('_')
  const ksSig = selectedKnockStatusIds.join('_')

  const result = useMemo(() => {
    if (!enabled || !records || records.length === 0) return null
    return computeHiddenByFilters(
      records,
      { selectedTypes, selectedKnockStatusIds, myRecords },
      currentUser?.userId ?? null,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, records, typesSig, ksSig, myRecords, currentUser?.userId])

  const applyFilterUpdate = useCallback(() => {
    if (!result?.hiddenExists) return

    const batch: {
      type: IUserPreferenceGeneralListItemType
      value: string
    }[] = []

    if (result.changed.entityTypes) {
      batch.push({ type: PREF.entityTypes, value: result.update.entityTypes })
    }
    if (result.changed.knockStatusIds) {
      batch.push({
        type: PREF.knockStatuses,
        value: result.update.knockStatusIds,
      })
    }
    if (result.changed.myRecords) {
      batch.push({ type: PREF.myRecords, value: result.update.myRecords })
    }

    if (batch.length > 0) {
      dispatch(saveGeneralPreferencesBatch(batch))
    }
  }, [dispatch, result])

  return {
    hiddenExists: result?.hiddenExists ?? false,
    applyFilterUpdate,
  }
}
